import SafeSummaryTile from "../../components/common/SafeSummaryTile";
import { useEffect, useMemo, useState } from "react";
import Pagination from "@mui/material/Pagination";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import WorkIcon from "@mui/icons-material/Work";
import { toast } from "react-toastify";

import { axiosPrivate as axiosInstance } from "../../../../api/axios";
import { SafePageHeader } from "../../components/common/HelperFunc";

const EMP_STATUS = [
  { value: 0, label: "재직", color: "success" },
  { value: 1, label: "휴직", color: "warning" },
  { value: 2, label: "퇴사", color: "default" },
];

const EMPTY_EMPLOYEE = {
  emp_no: "",
  user_no: "",
  login_id: "",
  user_name: "",
  team_id: "",
  position_id: "",
  emp_status: 0,
  hire_date: "",
  retire_date: "",
  retire_reason: "",
};

const EMPTY_TEAM = {
  team_name: "",
};

const EMPTY_POSITION = {
  position_index: 0,
  position_name: "",
};

const INITIAL_TEAM_SELECTION = "__first_team__";
const SUMMARY_FILTER_SELECTION = "__summary_filter__";

const apiPath = (groupCode, projectCode, path = "") =>
  `/emp/${groupCode}/${projectCode}/organization${path}`;

const getStatusMeta = (status) =>
  EMP_STATUS.find((item) => item.value === Number(status)) || EMP_STATUS[0];

const toFormValue = (value) => (value === null || value === undefined ? "" : value);

const normalizeEmployeeForForm = (employee) => {
  const source = employee || EMPTY_EMPLOYEE;

  return {
    emp_no: toFormValue(source.emp_no),
    user_no: toFormValue(source.user_no),
    login_id: toFormValue(source.login_id),
    user_name: toFormValue(source.user_name),
    team_id: toFormValue(source.team_id),
    position_id: toFormValue(source.position_id),
    emp_status: Number(source.emp_status || 0),
    hire_date: source.hire_date || "",
    retire_date: source.retire_date || "",
    retire_reason: source.retire_reason || "",
  };
};

const SummaryCard = (props) => <SafeSummaryTile {...props} label={props.title} value={`${props.value}명`} />;

const TeamCard = ({ team, employees, selectedTeamId, onSelect }) => {
  const theme = useTheme();
  const active = selectedTeamId === team.team_id;
  const activeCount = employees.filter((employee) => employee.emp_status === 0).length;

  return (
    <Paper
      elevation={0}
      onClick={() => onSelect(team.team_id)}
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor: active ? "primary.main" : "divider",
        borderRadius: "0.375rem",
        bgcolor: active ? alpha(theme.palette.primary.main, 0.06) : "#fff",
        cursor: "pointer",
        transition: "150ms ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.25}>
        <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
          <Avatar
            variant="rounded"
            sx={{
              width: 34,
              height: 34,
              bgcolor: alpha(theme.palette.info.main, 0.1),
              color: "info.main",
            }}
          >
            <BusinessIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={900} noWrap>
              {team.team_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              재직 {activeCount}명 · 전체 {employees.length}명
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
          <Chip
            size="small"
            color={active ? "primary" : "default"}
            variant={active ? "filled" : "outlined"}
            label={`${employees.length}명`}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

const EmployeeDialog = ({ open, employee, teams, positions, onClose, onSubmit, loading }) => {
  const [form, setForm] = useState(() => normalizeEmployeeForForm(employee));

  useEffect(() => {
    setForm(normalizeEmployeeForForm(employee));
  }, [employee, open]);

  const setValue = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const isRetired = Number(form.emp_status) === 2;

  const handleSubmit = () => {
    onSubmit({
      ...form,
      user_no: form.user_no === "" ? null : Number(form.user_no),
      team_id: form.team_id === "" ? null : Number(form.team_id),
      position_id: form.position_id === "" ? null : Number(form.position_id),
      emp_status: Number(form.emp_status),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>
        {employee?.emp_id ? "근로자 정보 수정" : "근로자 등록"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="근로자명"
              value={form.user_name}
              onChange={setValue("user_name")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="사번"
              value={form.emp_no}
              onChange={setValue("emp_no")}
              placeholder="예: E2026-001"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>재직 상태</InputLabel>
              <Select
                label="재직 상태"
                value={form.emp_status}
                onChange={setValue("emp_status")}
              >
                {EMP_STATUS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>팀/부서</InputLabel>
              <Select label="팀/부서" value={form.team_id} onChange={setValue("team_id")}>
                <MenuItem value="">미지정</MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.team_id} value={team.team_id}>
                    {team.team_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>직급</InputLabel>
              <Select
                label="직급"
                value={form.position_id}
                onChange={setValue("position_id")}
              >
                <MenuItem value="">미지정</MenuItem>
                {positions.map((position) => (
                  <MenuItem key={position.position_id} value={position.position_id}>
                    {position.position_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="입사일"
              value={form.hire_date}
              onChange={setValue("hire_date")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {employee?.emp_id && isRetired && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="퇴사일"
                value={form.retire_date}
                onChange={setValue("retire_date")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          {employee?.emp_id && isRetired && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="퇴사 사유"
                value={form.retire_reason}
                onChange={setValue("retire_reason")}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          취소
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SimpleEntityDialog = ({
  open,
  title,
  type,
  value,
  onClose,
  onSubmit,
  loading,
}) => {
  const [form, setForm] = useState(value || {});

  useEffect(() => {
    setForm(value || {});
  }, [value, open]);

  const isPosition = type === "position";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {isPosition && (
            <TextField
              fullWidth
              type="number"
              label="표시 순서"
              value={form.position_index ?? 0}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, position_index: event.target.value }))
              }
            />
          )}
          <TextField
            fullWidth
            required
            label={isPosition ? "직급명" : "팀/부서명"}
            value={isPosition ? form.position_name || "" : form.team_name || ""}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                [isPosition ? "position_name" : "team_name"]: event.target.value,
              }))
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          취소
        </Button>
        <Button variant="contained" onClick={() => onSubmit(form)} disabled={loading}>
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const OrganizationManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { group_code: groupCode, project_code: projectCode } = useParams();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [employeePage, setEmployeePage] = useState(1);
  const compactList = useMediaQuery(theme.breakpoints.down("lg"));
  const [selectedTeamId, setSelectedTeamId] = useState(INITIAL_TEAM_SELECTION);
  const [summaryFilter, setSummaryFilter] = useState(null);
  const [tab, setTab] = useState("employees");
  const [employeeDialog, setEmployeeDialog] = useState(null);
  const [teamDialog, setTeamDialog] = useState(null);
  const [positionDialog, setPositionDialog] = useState(null);

  const organizationQuery = useQuery({
    queryKey: ["organization", groupCode, projectCode],
    queryFn: async () => {
      const { data } = await axiosInstance.get(apiPath(groupCode, projectCode));
      return data;
    },
    enabled: Boolean(groupCode && projectCode),
  });

  const organization = organizationQuery.data || {
    teams: [],
    positions: [],
    employees: [],
    summary: {},
  };

  useEffect(() => {
    if (!organization.teams.length) return;
    if (summaryFilter) return;

    const teamIds = new Set(organization.teams.map((team) => team.team_id));
    const needsInitialTeam =
      selectedTeamId === INITIAL_TEAM_SELECTION ||
      (selectedTeamId !== "all" &&
        selectedTeamId !== null &&
        selectedTeamId !== SUMMARY_FILTER_SELECTION &&
        !teamIds.has(selectedTeamId));

    if (needsInitialTeam) {
      setSelectedTeamId(organization.teams[0].team_id);
    }
  }, [organization.teams, selectedTeamId, summaryFilter]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["organization", groupCode, projectCode] });

  const mutation = useMutation({
    mutationFn: async ({ method, path, payload }) => {
      const { data } = await axiosInstance({
        method,
        url: apiPath(groupCode, projectCode, path),
        data: payload,
      });
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "저장되었습니다.");
      invalidate();
      setEmployeeDialog(null);
      setTeamDialog(null);
      setPositionDialog(null);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "처리 중 오류가 발생했습니다.");
    },
  });
  const isSaving = Boolean(mutation.isPending || mutation.isLoading);

  const employeesByTeam = useMemo(() => {
    const map = new Map();
    organization.teams.forEach((team) => map.set(team.team_id, []));
    map.set(null, []);

    organization.employees.forEach((employee) => {
      const key = employee.team_id || null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(employee);
    });

    return map;
  }, [organization.employees, organization.teams]);

  const filteredEmployees = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return organization.employees.filter((employee) => {
      const matchesSummary =
        !summaryFilter ||
        summaryFilter === "all" ||
        (summaryFilter === "active" && Number(employee.emp_status) === 0) ||
        (summaryFilter === "leave" && Number(employee.emp_status) === 1) ||
        (summaryFilter === "retired" && Number(employee.emp_status) === 2);
      if (!matchesSummary) return false;

      const matchesTeam = summaryFilter
        ? true
        : selectedTeamId === "all" ||
          (selectedTeamId === null && !employee.team_id) ||
          employee.team_id === selectedTeamId;
      if (!matchesTeam) return false;
      if (!keyword) return true;
      return [
        employee.user_name,
        employee.emp_no,
        employee.team_name,
        employee.position_name,
        employee.emp_status_label,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword));
    });
  }, [organization.employees, search, selectedTeamId, summaryFilter]);

  useEffect(() => setEmployeePage(1), [search, selectedTeamId, summaryFilter]);
  const employeePageCount = Math.max(1, Math.ceil(filteredEmployees.length / 10));
  const currentEmployeePage = Math.min(employeePage, employeePageCount);
  const visibleEmployees = compactList ? filteredEmployees.slice((currentEmployeePage - 1) * 10, currentEmployeePage * 10) : filteredEmployees;

  const selectTeam = (teamId) => {
    setSummaryFilter(null);
    setSelectedTeamId(teamId);
    setTab("employees");
  };

  const toggleSummaryFilter = (filter) => {
    setTab("employees");
    if (summaryFilter === filter) {
      setSummaryFilter(null);
      setSelectedTeamId(organization.teams[0]?.team_id || INITIAL_TEAM_SELECTION);
      return;
    }
    setSummaryFilter(filter);
    setSelectedTeamId(SUMMARY_FILTER_SELECTION);
  };

  const submitEmployee = (payload) => {
    const employee = employeeDialog?.employee;
    mutation.mutate({
      method: employee?.emp_id ? "put" : "post",
      path: employee?.emp_id ? `/employees/${employee.emp_id}` : "/employees",
      payload,
    });
  };

  const submitTeam = (payload) => {
    const team = teamDialog?.team;
    mutation.mutate({
      method: team?.team_id ? "put" : "post",
      path: team?.team_id ? `/teams/${team.team_id}` : "/teams",
      payload,
    });
  };

  const deleteTeam = (team) => {
    if (!window.confirm(`${team.team_name} 팀/부서를 삭제할까요? 배정된 근로자는 미지정으로 남습니다.`)) {
      return;
    }
    mutation.mutate({ method: "delete", path: `/teams/${team.team_id}` });
  };

  const submitPosition = (payload) => {
    const position = positionDialog?.position;
    mutation.mutate({
      method: position?.position_id ? "put" : "post",
      path: position?.position_id ? `/positions/${position.position_id}` : "/positions",
      payload: {
        ...payload,
        position_index: Number(payload.position_index || 0),
      },
    });
  };

  const deletePosition = (position) => {
    if (!window.confirm(`${position.position_name} 직급을 삭제할까요? 배정된 근로자는 직급 미지정으로 남습니다.`)) {
      return;
    }
    mutation.mutate({ method: "delete", path: `/positions/${position.position_id}` });
  };

  return (
    <Box className="safe-page-root"
      sx={{
        width: "100%",
        height: { xs: "auto", lg: "100%" },
        minHeight: { xs: "100%", lg: 0 },
        flex: 1,
        p: { xs: 2, md: "16px 32px" },
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: { xs: "auto", lg: "hidden" },
        bgcolor: "background.default",
      }}
    >
      <SafePageHeader title="조직·권한 관리" actions={<Button
          variant="contained"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setEmployeeDialog({ employee: null })}
        >
          근로자 등록
        </Button>}></SafePageHeader>

      {organizationQuery.isError && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          조직 정보를 불러오지 못했습니다. EmpRouter 연결과 DB 권한을 확인해주세요.
        </Alert>
      )}

      <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}>
          <SummaryCard
            icon={<GroupsIcon />}
            title="전체"
            value={organization.summary.total || 0}
            active={summaryFilter === "all"}
            onClick={() => toggleSummaryFilter("all")}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            icon={<BadgeIcon />}
            title="재직"
            value={organization.summary.active || 0}
            tone="success"
            active={summaryFilter === "active"}
            onClick={() => toggleSummaryFilter("active")}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            icon={<WorkIcon />}
            title="휴직"
            value={organization.summary.leave || 0}
            tone="warning"
            active={summaryFilter === "leave"}
            onClick={() => toggleSummaryFilter("leave")}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            icon={<CloseIcon />}
            title="퇴사"
            value={organization.summary.retired || 0}
            tone="grey"
            active={summaryFilter === "retired"}
            onClick={() => toggleSummaryFilter("retired")}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={1.5}
        sx={{
          flex: { xs: "0 0 auto", lg: 1 },
          minHeight: { xs: "auto", lg: 0 },
          alignItems: { xs: "flex-start", lg: "stretch" },
          overflow: { xs: "visible", lg: "hidden" },
          pb: { xs: 2, lg: 0 },
        }}
      >
        <Grid item xs={12} lg={5} sx={{ minHeight: { xs: "auto", lg: 0 }, display: "flex" }}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              height: { xs: "auto", lg: "100%" },
              minHeight: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "0.375rem",
              bgcolor: "#fff",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <AccountTreeIcon color="primary" />
                <Typography fontWeight={900}>조직도</Typography>
              </Stack>
              <Chip
                size="small"
                color="primary"
                variant={!summaryFilter && selectedTeamId === "all" ? "filled" : "outlined"}
                label="전체"
                onClick={() => selectTeam("all")}
              />
            </Stack>

            <Box
              sx={{
                p: 1.5,
                overflowY: "auto",
                overflowX: "hidden",
                flex: { xs: "0 0 auto", lg: 1 },
                minHeight: { xs: "auto", lg: 0 },
                maxHeight: { xs: 320, sm: 360, lg: "none" },
                WebkitOverflowScrolling: "touch",
              }}
            >
              {organizationQuery.isLoading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Stack spacing={1.25}>
                  {organization.teams.map((team) => (
                    <TeamCard
                      key={team.team_id}
                      team={team}
                      employees={employeesByTeam.get(team.team_id) || []}
                      selectedTeamId={selectedTeamId}
                      onSelect={selectTeam}
                    />
                  ))}
                  {(employeesByTeam.get(null) || []).length > 0 && (
                    <TeamCard
                      team={{ team_id: null, team_name: "팀/부서 미지정" }}
                      employees={employeesByTeam.get(null) || []}
                      selectedTeamId={selectedTeamId}
                      onSelect={selectTeam}
                    />
                  )}
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          lg={7}
          sx={{
            minHeight: { xs: "auto", lg: 0 },
            height: { xs: "auto", lg: "100%" },
            display: "flex",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              height: { xs: "auto", lg: "100%" },
              minHeight: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "0.375rem",
              bgcolor: "#fff",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Tabs
                value={tab}
                onChange={(_, value) => setTab(value)}
                variant={isMobile ? "fullWidth" : "standard"}
                sx={{ minWidth: 0, maxWidth: "100%", "& .MuiTab-root": { minWidth: 0 } }}
              >
                <Tab value="employees" label="근로자" />
                <Tab value="teams" label="팀/부서" />
                <Tab value="positions" label="직급" />
              </Tabs>

              {tab === "employees" && (
                <TextField
                  size="small"
                  placeholder="이름, 사번, 팀, 직급 검색"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 0, width: { xs: "100%", md: 280 }, maxWidth: "100%" }}
                />
              )}
            </Stack>

            <Box
              sx={{
                flex: { xs: "0 0 auto", lg: 1 },
                minHeight: { xs: "auto", lg: 0 },
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {tab === "employees" && (
                <Stack spacing={1} sx={{ p: 1.5 }}>
                  {visibleEmployees.map((employee) => {
                    const status = getStatusMeta(employee.emp_status);
                    return (
                      <Paper
                        key={employee.emp_id}
                        className="safe-mobile-record"
                        elevation={0}
                        sx={{
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "0.375rem",
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          alignItems={{ xs: "stretch", sm: "center" }}
                          justifyContent="space-between"
                          gap={1.25}
                        >
                          <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                              {(employee.user_name || "?").slice(0, 1)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                                <Typography fontWeight={900}>{employee.user_name}</Typography>
                                {employee.emp_no && (
                                  <Chip size="small" variant="outlined" label={`사번 ${employee.emp_no}`} />
                                )}
                                <Chip size="small" color={status.color} label={status.label} />
                              </Stack>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {employee.team_name || "팀/부서 미지정"} ·{" "}
                                {employee.position_name || "직급 미지정"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                입사일 {employee.hire_date || "미등록"}
                                {employee.emp_status === 2 && employee.retire_date
                                  ? ` · 퇴사일 ${employee.retire_date}`
                                  : ""}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" alignItems="center" gap={0.5} justifyContent="flex-end">
                            <Tooltip title="수정">
                              <IconButton
                                size="small"
                                aria-label={`${employee.user_name} 수정`}
                                onClick={() => setEmployeeDialog({ employee })}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                  {compactList && filteredEmployees.length > 10 && <Pagination count={employeePageCount} page={currentEmployeePage}
                    onChange={(_, value) => setEmployeePage(value)} siblingCount={0} size="small" sx={{ alignSelf: "center" }} />}
                  {!filteredEmployees.length && (
                    <Box
                      sx={{
                        py: 8,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: "0.375rem",
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <Typography>표시할 근로자가 없습니다.</Typography>
                    </Box>
                  )}
                </Stack>
              )}

              {tab === "teams" && (
                <Stack spacing={1} sx={{ p: 1.5 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setTeamDialog({ team: null })}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    팀/부서 추가
                  </Button>
                  {organization.teams.map((team) => (
                    <Paper
                      key={team.team_id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "0.375rem",
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                          <BusinessIcon color="info" />
                          <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            <Typography fontWeight={900}>{team.team_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(employeesByTeam.get(team.team_id) || []).length}명 배정
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
                          <IconButton size="small" onClick={() => setTeamDialog({ team })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteTeam(team)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}

              {tab === "positions" && (
                <Stack spacing={1} sx={{ p: 1.5 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setPositionDialog({ position: null })}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    직급 추가
                  </Button>
                  {organization.positions.map((position) => (
                    <Paper
                      key={position.position_id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "0.375rem",
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                          <WorkIcon color="primary" />
                          <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            <Typography fontWeight={900}>{position.position_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              표시 순서 {position.position_index}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
                          <IconButton size="small" onClick={() => setPositionDialog({ position })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deletePosition(position)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {employeeDialog && (
        <EmployeeDialog
          open={Boolean(employeeDialog)}
          employee={employeeDialog.employee}
          teams={organization.teams}
          positions={organization.positions}
          onClose={() => setEmployeeDialog(null)}
          onSubmit={submitEmployee}
          loading={isSaving}
        />
      )}

      {teamDialog && (
        <SimpleEntityDialog
          open={Boolean(teamDialog)}
          title={teamDialog.team?.team_id ? "팀/부서 수정" : "팀/부서 추가"}
          type="team"
          value={teamDialog.team || EMPTY_TEAM}
          onClose={() => setTeamDialog(null)}
          onSubmit={submitTeam}
          loading={isSaving}
        />
      )}

      {positionDialog && (
        <SimpleEntityDialog
          open={Boolean(positionDialog)}
          title={positionDialog.position?.position_id ? "직급 수정" : "직급 추가"}
          type="position"
          value={positionDialog.position || EMPTY_POSITION}
          onClose={() => setPositionDialog(null)}
          onSubmit={submitPosition}
          loading={isSaving}
        />
      )}
    </Box>
  );
};
